import React, { createContext, useCallback, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { api, watchSiteChanges } from '../lib/api';

const SiteContext = createContext({ settings: null, refresh: async () => {} });
export const useSite = () => useContext(SiteContext);

export function SiteProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const requestId = useRef(0);
  const refresh = useCallback(async () => {
    const id = ++requestId.current;
    const { data } = await api.get('/public/settings');
    if (id === requestId.current) setSettings(data.settings);
  }, []);
  useEffect(() => {
    const update = () => refresh().catch(() => console.warn('Website settings could not be refreshed.'));
    update();
    const stopWatching = watchSiteChanges(update);
    const visible = () => { if (document.visibilityState === 'visible') update(); };
    document.addEventListener('visibilitychange', visible);
    const timer = setInterval(visible, 5000);
    return () => { requestId.current += 1; clearInterval(timer); stopWatching(); document.removeEventListener('visibilitychange', visible); };
  }, [refresh]);
  useEffect(() => {
    if (!settings) return;
    const pageManaged = document.documentElement.hasAttribute('data-cms-seo-managed');
    if (!pageManaged) document.title = settings.seo.title;
    for (const [name, content] of Object.entries(pageManaged ? {} : { description: settings.seo.description, robots: settings.seo.indexable ? 'index, follow' : 'noindex, nofollow' })) {
      let node = document.querySelector(`meta[name="${name}"]`);
      if (!node) { node = document.createElement('meta'); node.name = name; document.head.appendChild(node); }
      node.content = content;
    }
    document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach(node => { node.href = settings.branding.faviconUrl || '/favicon.ico'; });
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!pageManaged && settings.seo.canonicalUrl) {
      if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
      canonical.href = settings.seo.canonicalUrl;
    } else if (!pageManaged) canonical?.remove();
  }, [settings]);
  // LocaleProvider owns the interface language, including its settings default.
  // Do not overwrite a saved customer language when public settings finish loading.
  const value = useMemo(() => ({ settings, refresh }), [settings, refresh]);
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}